import type { IconElement } from '@backstage/frontend-plugin-api';
import {
  RiArrowDownSLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiCheckLine,
  RiDownloadCloud2Line,
  RiFlowChart,
} from '@remixicon/react';
import {
  ArgocdLogo,
  GithubIcon,
  GitlabLogo,
  GrafanaLogo,
  PostgresqlIcon,
} from './brandIcons';
import { PodTerminalIcon } from './terminalIcons';
import { EphemeralGhostIcon } from './ghostIcon';
import { waadooIcons } from './waadooIcons';
import { APP_LOGOS, type AppLogoSlug } from './appLogos';
import { AppInstanceIcon, AppLogoIcon } from './appLogoIcons';

// Le contenu du registre d'icônes de l'application, sans son enveloppe :
// IconBundleBlueprint est réservé au plugin `app` (modules/icons.tsx, qui
// l'assemble avec les entrées propres à Odoo,
// components/odooIcons/iconBundle.tsx). Mis à part à l'étape 9c du plan
// « plateforme d'applications », pour que les tests du cœur en tirent leur
// registre (components/shared/testing/testIcons.tsx).

/**
 * Icons registered under app-wide names, the way Backstage recommends: cards
 * ask for `refresh` or `external-link` through iconsApiRef and never import an
 * icon library themselves.
 *
 * Every entry is an ELEMENT (`<Icon />`), not a component: IconBundleBlueprint
 * accepts both, but DefaultIconsApi warns about every key registered as a
 * component. A name that is also a notion of the app vocabulary takes its
 * glyph from waadooIcons.ts, so the registry and the views cannot drift apart;
 * the others are Remix glyphs of the same line style.
 *
 * Remix icons are 24px by default, the size Backstage expects of an icon
 * element; the caller sizes them (see IconBox, components/ui). They carry no
 * `aria-hidden` of their own, hence the one set here: every use of these
 * names is decorative, next to words that say the same.
 */
const decorative = { 'aria-hidden': true, focusable: 'false' } as const;

// A coloured dot, not a status icon: it inherits the surrounding text colour,
// so the caller sets the meaning by wrapping it in a coloured <Text>. Drawn
// by hand because no Remix glyph is this small a disc (radius 6 of 24), and
// replacing it would change the look of every status line.
const statusDot = (
  <svg
    viewBox="0 0 24 24"
    width={24}
    height={24}
    fill="currentColor"
    {...decorative}
  >
    <circle cx="12" cy="12" r="6" />
  </svg>
);

const Refresh = waadooIcons.refresh;
const ExternalLink = waadooIcons.externalLink;
const Branch = waadooIcons.branch;
const BackupNow = waadooIcons.backupNow;
const Upload = waadooIcons.upload;
const Restore = waadooIcons.restore;
const Copy = waadooIcons.copy;
const Show = waadooIcons.show;
const Hide = waadooIcons.hide;
const Ci = waadooIcons.ci;
const Monitoring = waadooIcons.monitoring;
const Login = waadooIcons.login;
const Pause = waadooIcons.pause;
const Resume = waadooIcons.resume;

/**
 * The logo of every application of appLogos.ts under its slug, and the glyph
 * of an instance's address under `<slug>-instance`: the keys
 * `appLogoKey` and `appInstanceKey` give (shared/appIcons.ts), so that every
 * type with a logo gets it wherever its type is shown, without a module.
 *
 * Spread FIRST, and this bundle registered before the app type modules'
 * (packages/app/src/App.tsx, `iconsModule` before the modules): plugin-app's
 * IconsApi merges the bundles in the order of its `icons` input, the later
 * winning (`IconsApi.esm.js`, a spread per bundle), so a module's own glyph
 * for its type's keys replaces this one (App.test.tsx checks it).
 */
const appLogoIcons: Record<string, IconElement> = Object.fromEntries(
  (Object.keys(APP_LOGOS) as AppLogoSlug[]).flatMap(slug => [
    [slug, <AppLogoIcon slug={slug} />],
    [`${slug}-instance`, <AppInstanceIcon />],
  ]),
);

export const waadooIconBundle: Readonly<Record<string, IconElement>> = {
  ...appLogoIcons,
  refresh: <Refresh {...decorative} />,
  'external-link': <ExternalLink {...decorative} />,
  'status-dot': statusDot,
  // The mark on the entry one is already on, in a menu that offers a
  // choice. Decorative: the row carries the meaning in `aria-checked`.
  check: <RiCheckLine {...decorative} />,
  login: <Login {...decorative} />,
  monitoring: <Monitoring {...decorative} />,
  pipeline: <Ci {...decorative} />,
  // A pod's terminal: in colour, 1em and aria-hidden of its own
  // (terminalIcons.tsx), like the official logos below. The Odoo shell's
  // own button is `odoo-shell`, in components/odooIcons/iconBundle.tsx.
  terminal: <PodTerminalIcon />,
  // The two kinds of Odoo branch, for callers outside this app's views
  // (catalog-info link icons, upstream plugins): BranchLabel.tsx takes
  // its glyphs from BRANCH_KIND_ICONS (waadooIcons) directly. An
  // ephemeral branch is drawn with the same fork in its tier's violet
  // (styles.css): its mark of its own is the ghost of its badge,
  // in colour, 1em and aria-hidden of its own (ghostIcon.tsx).
  branch: <Branch {...decorative} />,
  'branch-ephemeral': <EphemeralGhostIcon />,
  // The glyphs of the action buttons and tool links of the Odoo
  // branch page. `workflow` stands for Argo Workflows: `pipeline`
  // already means the forge's CI, and a run of a workflow is not one.
  'chevron-down': <RiArrowDownSLine {...decorative} />,
  'chevron-left': <RiArrowLeftSLine {...decorative} />,
  'chevron-right': <RiArrowRightSLine {...decorative} />,
  save: <BackupNow {...decorative} />,
  upload: <Upload {...decorative} />,
  download: <RiDownloadCloud2Line {...decorative} />,
  restore: <Restore {...decorative} />,
  copy: <Copy {...decorative} />,
  visibility: <Show {...decorative} />,
  'visibility-off': <Hide {...decorative} />,
  workflow: <RiFlowChart {...decorative} />,
  pause: <Pause {...decorative} />,
  play: <Resume {...decorative} />,
  // Brand logos. Registering them here also makes them usable from a
  // catalog-info.yaml, where `metadata.links[].icon` resolves against
  // this very registry — that is how app1 links get the GitLab and
  // ArgoCD logos instead of the generic built-in glyphs.
  //
  // `github` deliberately shadows Backstage's own built-in icon of that
  // name. Not an accident and not a collision to rename away: the
  // environments card picks its forge logo by name, and the built-in
  // glyph would sit in a row next to the GitLab, ArgoCD and Grafana
  // marks without belonging to the same visual family.
  //
  // GitHub and PostgreSQL (and Odoo, components/odooIcons/iconBundle.tsx)
  // are Material UI SvgIcons, hence
  // `fontSize="inherit"`, which the IconElement contract asks of
  // them; SvgIcon sets aria-hidden itself. GitLab, ArgoCD and Grafana
  // are the official multicolour logos, 1em and aria-hidden of their
  // own (brandIcons.tsx): no colour set around them changes them.
  github: <GithubIcon fontSize="inherit" />,
  gitlab: <GitlabLogo />,
  argocd: <ArgocdLogo />,
  grafana: <GrafanaLogo />,
  postgresql: <PostgresqlIcon fontSize="inherit" />,
};
